import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { Menu, X, LogOut, LayoutDashboard, UserPlus, LogIn, Music, Search, Briefcase } from 'lucide-react';

const Header = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const menuItems = user ? (
    <>
      <Link 
        to="/admin" 
        onClick={() => setMobileMenuOpen(false)}
        className="group flex items-center space-x-3 px-4 py-3 text-gray-200 hover:text-white hover:bg-blue-600/20 rounded-lg transition-all duration-200 border border-transparent hover:border-blue-500/30"
      >
        <div className="p-2 bg-blue-600/20 rounded-lg group-hover:bg-blue-600/30 transition-colors">
          <LayoutDashboard className="w-4 h-4 text-blue-400" />
        </div>
        <span className="font-medium">Dashboard</span>
      </Link>
      <button 
        onClick={() => {
          handleLogout();
          setMobileMenuOpen(false);
        }} 
        className="group flex items-center space-x-3 px-4 py-3 text-red-300 hover:text-red-200 hover:bg-red-600/20 rounded-lg transition-all duration-200 border border-transparent hover:border-red-500/30"
      >
        <div className="p-2 bg-red-600/20 rounded-lg group-hover:bg-red-600/30 transition-colors">
          <LogOut className="w-4 h-4 text-red-400" />
        </div>
        <span className="font-medium">Logout</span>
      </button>
    </>
  ) : (
    <>
      <Link 
        to="/signup" 
        onClick={() => setMobileMenuOpen(false)}
        className="group flex items-center space-x-3 px-4 py-3 text-gray-200 hover:text-white hover:bg-blue-600/20 rounded-lg transition-all duration-200 border border-transparent hover:border-blue-500/30"
      >
        <div className="p-2 bg-blue-600/20 rounded-lg group-hover:bg-blue-600/30 transition-colors">
          <UserPlus className="w-4 h-4 text-blue-400" />
        </div>
        <span className="font-medium">Sign Up</span>
      </Link>
      <Link 
        to="/login" 
        onClick={() => setMobileMenuOpen(false)}
        className="group flex items-center space-x-3 px-4 py-3 text-gray-200 hover:text-white hover:bg-purple-600/20 rounded-lg transition-all duration-200 border border-transparent hover:border-purple-500/30"
      >
        <div className="p-2 bg-purple-600/20 rounded-lg group-hover:bg-purple-600/30 transition-colors">
          <LogIn className="w-4 h-4 text-purple-400" />
        </div>
        <span className="font-medium">Log In</span>
      </Link>
      <div className="rounded-lg border border-pink-500/25 bg-pink-950/10 overflow-hidden">
        <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-pink-300/95 border-b border-pink-500/20">
          Karaoke
        </p>
        <div className="p-1">
          <Link
            to="/karaoke"
            onClick={() => setMobileMenuOpen(false)}
            className="group flex items-center space-x-3 px-3 py-2.5 text-gray-200 hover:text-white hover:bg-pink-600/20 rounded-lg transition-all duration-200 border border-transparent hover:border-pink-500/30"
          >
            <div className="p-2 bg-pink-600/20 rounded-lg group-hover:bg-pink-600/30 transition-colors">
              <Music className="w-4 h-4 text-pink-400" />
            </div>
            <span className="font-medium">Karaoke Mode</span>
          </Link>
        </div>
      </div>
      <div className="rounded-lg border border-blue-500/25 bg-blue-950/10 overflow-hidden">
        <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-blue-300/95 border-b border-blue-500/20">
          Agents
        </p>
        <div className="p-1">
          <Link
            to="/agent"
            onClick={() => setMobileMenuOpen(false)}
            className="group flex items-center space-x-3 px-3 py-2.5 text-gray-200 hover:text-white hover:bg-blue-600/20 rounded-lg transition-all duration-200 border border-transparent hover:border-blue-500/30"
          >
            <div className="p-2 bg-blue-600/20 rounded-lg group-hover:bg-blue-600/30 transition-colors">
              <Briefcase className="w-4 h-4 text-blue-400" />
            </div>
            <span className="font-medium">For Booking Agents</span>
          </Link>
        </div>
      </div>
      <Link 
        to="/find-performer" 
        onClick={() => setMobileMenuOpen(false)}
        className="group flex items-center space-x-3 px-4 py-3 text-gray-200 hover:text-white hover:bg-green-600/20 rounded-lg transition-all duration-200 border border-transparent hover:border-green-500/30"
      >
        <div className="p-2 bg-green-600/20 rounded-lg group-hover:bg-green-600/30 transition-colors">
          <Search className="w-4 h-4 text-green-400" />
        </div>
        <span className="font-medium">Find a YSR Performer</span>
      </Link>
    </>
  );

  return (
    <header className="absolute top-0 left-0 right-0 z-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <Link to="/" className="text-3xl font-bold text-white" style={{ textShadow: '0 0 10px rgba(255, 255, 255, 0.7), 0 0 20px rgba(59, 130, 246, 0.5)' }}>
          YSR
        </Link>
        <nav className="hidden md:flex items-center space-x-4">
          {user && (
            <>
              <Link to="/admin">
                <Button variant="ghost">Dashboard</Button>
              </Link>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </>
          )}
        </nav>
        {/* Mobile Hamburger Menu */}
        <div className="md:hidden">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="relative z-50"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
        {/* Desktop Hamburger Menu */}
        {!user && (
          <div className="hidden md:block">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="relative z-50 hover:bg-gray-800/50 transition-colors"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        )}
      </div>
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />
            {/* Menu */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="absolute top-full right-4 mt-2 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 backdrop-blur-xl rounded-xl p-2 border border-gray-700/50 shadow-2xl min-w-[240px] z-50"
              style={{ boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.2)' }}
            >
              <nav className="flex flex-col space-y-1">
                {menuItems}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
